import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, Ship, Plane, Truck, BarChart3, RefreshCw, 
  Info, Plus, Edit, Trash2, Save, Calendar, AlertTriangle, Clock,
  Container, Car, Package, Search, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import portsByCountry, { getCountriesSorted } from '../data/ports';

// Container and equipment types
const equipmentTypes = [
  { value: '20ft_standard', label: "20' Standard Container", category: 'container' },
  { value: '40ft_standard', label: "40' Standard Container", category: 'container' },
  { value: '40ft_high_cube', label: "40' High Cube Container", category: 'container' },
  { value: '20ft_reefer', label: "20' Reefer Container", category: 'container' },
  { value: '40ft_reefer', label: "40' Reefer Container", category: 'container' },
  { value: '20ft_open_top', label: "20' Open Top Container", category: 'container' },
  { value: '40ft_open_top', label: "40' Open Top Container", category: 'container' },
  { value: '20ft_flat_rack', label: "20' Flat Rack", category: 'container' },
  { value: '40ft_flat_rack', label: "40' Flat Rack", category: 'container' },
  { value: '20ft_tank', label: "20' Tank Container", category: 'tank' },
  { value: '40ft_tank', label: "40' Tank Container", category: 'tank' },
  { value: 'iso_tank', label: 'ISO Tank Container', category: 'tank' },
  { value: 'vehicle_sedan', label: 'Vehicle - Sedan/Car', category: 'vehicle' },
  { value: 'vehicle_suv', label: 'Vehicle - SUV/Crossover', category: 'vehicle' },
  { value: 'vehicle_truck', label: 'Vehicle - Pickup Truck', category: 'vehicle' },
  { value: 'vehicle_van', label: 'Vehicle - Van', category: 'vehicle' },
  { value: 'vehicle_motorcycle', label: 'Vehicle - Motorcycle', category: 'vehicle' },
  { value: 'vehicle_heavy', label: 'Vehicle - Heavy Equipment', category: 'vehicle' },
  { value: 'vehicle_boat', label: 'Vehicle - Boat/Yacht', category: 'vehicle' },
  { value: 'breakbulk', label: 'Breakbulk Cargo', category: 'general' },
  { value: 'roro', label: 'RoRo Cargo', category: 'general' },
  { value: 'bulk_dry', label: 'Dry Bulk', category: 'bulk' },
  { value: 'bulk_liquid', label: 'Liquid Bulk', category: 'bulk' },
  { value: 'lcl', label: 'LCL (Less Container Load)', category: 'general' },
  { value: 'air_cargo_kg', label: 'Air Cargo (per kg)', category: 'air' },
  { value: 'air_cargo_cbm', label: 'Air Cargo (per cbm)', category: 'air' },
  { value: 'parcel', label: 'Parcel/Express', category: 'parcel' },
  { value: 'pallet', label: 'Pallet', category: 'general' },
  { value: 'hazmat', label: 'Hazardous Materials', category: 'special' },
  { value: 'perishable', label: 'Perishable Goods', category: 'special' },
  { value: 'oversized', label: 'Oversized/Project Cargo', category: 'special' },
];

const FreightInsights = () => {
  const { isAdmin } = useAuth();
  const [insights, setInsights] = useState([]);
  const [aggregatedInsights, setAggregatedInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMode, setFilterMode] = useState('all');
  const [filterEquipment, setFilterEquipment] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [expiringRates, setExpiringRates] = useState([]);
  
  // Admin form states
  const [showRateForm, setShowRateForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [rateForm, setRateForm] = useState({
    origin_country: '',
    origin_port: '',
    destination_country: '',
    destination_port: '',
    mode: 'sea',
    equipment_type: '20ft_standard',
    currency: 'USD',
    rate: '',
    notes: '',
    valid_from: '',
    valid_until: '',
    transit_time_days: '',
    carrier: ''
  });
  
  // Port search states
  const [originSearch, setOriginSearch] = useState('');
  const [destSearch, setDestSearch] = useState('');
  const [showOriginDropdown, setShowOriginDropdown] = useState(false);
  const [showDestDropdown, setShowDestDropdown] = useState(false);
  
  // Modal states
  const [showRoutesModal, setShowRoutesModal] = useState(false);
  const [routesModalMode, setRoutesModalMode] = useState('all');
  const [routesModalTitle, setRoutesModalTitle] = useState('');
  const [showExpiringModal, setShowExpiringModal] = useState(false);

  const countries = useMemo(() => getCountriesSorted(), []);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const [storedRes, aggregatedRes] = await Promise.all([
        api.get('/insights/rates').catch(() => ({ data: [] })),
        api.get('/insights/aggregate').catch(() => ({ data: [] }))
      ]);
      
      const allRates = storedRes.data || [];
      setInsights(allRates);
      setAggregatedInsights(aggregatedRes.data || []);
      
      // Check for expiring rates (within 7 days)
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const expiring = allRates.filter(rate => {
        if (!rate.valid_until) return false;
        const expiryDate = new Date(rate.valid_until);
        return expiryDate <= sevenDaysFromNow && expiryDate >= now;
      });
      setExpiringRates(expiring);
    } catch (error) {
      console.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const refreshInsights = async () => {
    setRefreshing(true);
    try {
      const response = await api.get('/insights/aggregate');
      setAggregatedInsights(response.data || []);
      toast.success('Insights refreshed');
    } catch (error) {
      toast.error('Failed to refresh insights');
    } finally {
      setRefreshing(false);
    }
  };

  // Port search filtering
  const filteredOriginPorts = useMemo(() => {
    if (!originSearch || originSearch.length < 2) return [];
    const searchLower = originSearch.toLowerCase();
    const results = [];
    
    Object.entries(portsByCountry).forEach(([country, portList]) => {
      portList.forEach(port => {
        if (port.name.toLowerCase().includes(searchLower) || 
            port.code.toLowerCase().includes(searchLower) ||
            country.toLowerCase().includes(searchLower)) {
          results.push({ ...port, country });
        }
      });
    });
    
    return results.slice(0, 15);
  }, [originSearch]);

  const filteredDestPorts = useMemo(() => {
    if (!destSearch || destSearch.length < 2) return [];
    const searchLower = destSearch.toLowerCase();
    const results = [];
    
    Object.entries(portsByCountry).forEach(([country, portList]) => {
      portList.forEach(port => {
        if (port.name.toLowerCase().includes(searchLower) || 
            port.code.toLowerCase().includes(searchLower) ||
            country.toLowerCase().includes(searchLower)) {
          results.push({ ...port, country });
        }
      });
    });
    
    return results.slice(0, 15);
  }, [destSearch]);

  const selectOriginPort = (port) => {
    setRateForm(prev => ({
      ...prev,
      origin_country: port.country,
      origin_port: port.code
    }));
    setOriginSearch(`${port.name} (${port.code}) - ${port.country}`);
    setShowOriginDropdown(false);
  };

  const selectDestPort = (port) => {
    setRateForm(prev => ({
      ...prev,
      destination_country: port.country,
      destination_port: port.code
    }));
    setDestSearch(`${port.name} (${port.code}) - ${port.country}`);
    setShowDestDropdown(false);
  };

  const handleRateSubmit = async (e) => {
    e.preventDefault();
    
    if (!rateForm.origin_port || !rateForm.destination_port) {
      toast.error('Please select origin and destination ports');
      return;
    }
    
    if (!rateForm.valid_from || !rateForm.valid_until) {
      toast.error('Please set validity period');
      return;
    }
    
    try {
      const payload = {
        ...rateForm,
        rate: parseFloat(rateForm.rate),
        transit_time_days: rateForm.transit_time_days ? parseInt(rateForm.transit_time_days) : null,
        lane: `${rateForm.origin_port}-${rateForm.destination_port}`
      };
      
      if (editingRate) {
        await api.put(`/admin/insights/rates/${editingRate.id}`, payload);
        toast.success('Rate updated successfully');
      } else {
        await api.post('/admin/insights/rates', payload);
        toast.success('Rate added successfully');
      }
      setShowRateForm(false);
      setEditingRate(null);
      resetForm();
      fetchInsights();
    } catch (error) {
      toast.error('Failed to save rate');
    }
  };

  const resetForm = () => {
    setRateForm({
      origin_country: '', origin_port: '', destination_country: '', destination_port: '',
      mode: 'sea', equipment_type: '20ft_standard', currency: 'USD', rate: '', notes: '',
      valid_from: '', valid_until: '', transit_time_days: '', carrier: ''
    });
    setOriginSearch('');
    setDestSearch('');
  };

  const editRate = (rate) => {
    setEditingRate(rate);
    setRateForm({
      origin_country: rate.origin_country || '',
      origin_port: rate.origin_port || '',
      destination_country: rate.destination_country || '',
      destination_port: rate.destination_port || '',
      mode: rate.mode || 'sea',
      equipment_type: rate.equipment_type || rate.cargo_type || '20ft_standard',
      currency: rate.currency || 'USD',
      rate: rate.rate?.toString() || rate.avg_rate?.toString() || '',
      notes: rate.notes || '',
      valid_from: rate.valid_from?.split('T')[0] || '',
      valid_until: rate.valid_until?.split('T')[0] || '',
      transit_time_days: rate.transit_time_days?.toString() || '',
      carrier: rate.carrier || ''
    });
    
    // Set search display
    if (rate.origin_port) {
      setOriginSearch(`${rate.origin_port} - ${rate.origin_country || ''}`);
    }
    if (rate.destination_port) {
      setDestSearch(`${rate.destination_port} - ${rate.destination_country || ''}`);
    }
    
    setShowRateForm(true);
  };

  const deleteRate = async (id) => {
    if (!window.confirm('Delete this rate?')) return;
    try {
      await api.delete(`/admin/insights/rates/${id}`);
      toast.success('Rate deleted');
      fetchInsights();
    } catch (error) {
      toast.error('Failed to delete rate');
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'air': return <Plane className="h-4 w-4" />;
      case 'sea': return <Ship className="h-4 w-4" />;
      case 'land': return <Truck className="h-4 w-4" />;
      default: return <BarChart3 className="h-4 w-4" />;
    }
  };

  const getModeColor = (mode) => {
    switch (mode) {
      case 'air': return 'bg-blue-100 text-blue-700';
      case 'sea': return 'bg-cyan-100 text-cyan-700';
      case 'land': return 'bg-amber-100 text-amber-700';
      default: return 'bg-purple-100 text-purple-700';
    }
  };

  const getEquipmentIcon = (type) => {
    if (type?.includes('vehicle') || type?.includes('car')) return <Car className="h-4 w-4" />;
    if (type?.includes('container') || type?.includes('ft')) return <Container className="h-4 w-4" />;
    return <Package className="h-4 w-4" />;
  };

  const getEquipmentLabel = (type) => {
    const eq = equipmentTypes.find(e => e.value === type);
    return eq?.label || type?.replace('_', ' ') || 'General';
  };

  const isRateExpiringSoon = (rate) => {
    if (!rate.valid_until) return false;
    const now = new Date();
    const expiryDate = new Date(rate.valid_until);
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return expiryDate <= sevenDaysFromNow && expiryDate >= now;
  };

  const isRateExpired = (rate) => {
    if (!rate.valid_until) return false;
    return new Date(rate.valid_until) < new Date();
  };

  // Combine and filter insights
  const allInsights = [...insights, ...aggregatedInsights];
  const uniqueInsights = allInsights.reduce((acc, curr) => {
    const key = `${curr.lane || `${curr.origin_port}-${curr.destination_port}`}-${curr.mode}-${curr.equipment_type || curr.cargo_type}`;
    if (!acc[key] || new Date(curr.last_updated) > new Date(acc[key].last_updated)) {
      acc[key] = curr;
    }
    return acc;
  }, {});

  const filteredInsights = Object.values(uniqueInsights).filter(i => {
    const modeMatch = filterMode === 'all' || i.mode === filterMode;
    const equipmentMatch = filterEquipment === 'all' || i.equipment_type === filterEquipment || i.cargo_type === filterEquipment;
    const notExpired = !isRateExpired(i);
    return modeMatch && equipmentMatch && notExpired;
  });

  const displayInsights = filteredInsights;

  const openRoutesModal = (mode, title) => {
    setRoutesModalMode(mode);
    setRoutesModalTitle(title);
    setShowRoutesModal(true);
  };

  const getFilteredRoutesForModal = () => {
    if (routesModalMode === 'all') return displayInsights;
    return displayInsights.filter(i => i.mode === routesModalMode);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-outfit font-bold text-ocean-900">Freight Rate Insights</h1>
              <p className="text-slate-600 mt-2">Current rates by lane, equipment type, and transport mode</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {isAdmin && expiringRates.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setShowExpiringModal(true)}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {expiringRates.length} Expiring
                </Button>
              )}
              {isAdmin && (
                <Button
                  onClick={() => {
                    setEditingRate(null);
                    resetForm();
                    setShowRateForm(true);
                  }}
                  className="bg-cyan-500 hover:bg-cyan-600"
                  data-testid="add-rate-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rate
                </Button>
              )}
              <Button
                variant="outline"
                onClick={refreshInsights}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All Modes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modes</SelectItem>
                <SelectItem value="air">Air</SelectItem>
                <SelectItem value="sea">Sea</SelectItem>
                <SelectItem value="land">Land</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterEquipment} onValueChange={setFilterEquipment}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Equipment" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">All Equipment Types</SelectItem>
                {equipmentTypes.map(eq => (
                  <SelectItem key={eq.value} value={eq.value}>{eq.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rate Form Dialog */}
          <Dialog open={showRateForm} onOpenChange={setShowRateForm}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingRate ? 'Edit Rate' : 'Add New Freight Rate'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleRateSubmit} className="space-y-6 pt-4">
                {/* Origin & Destination */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 relative">
                    <Label className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-green-500" />
                      Origin Port *
                    </Label>
                    <Input
                      value={originSearch}
                      onChange={(e) => {
                        setOriginSearch(e.target.value);
                        setShowOriginDropdown(true);
                      }}
                      onFocus={() => setShowOriginDropdown(true)}
                      placeholder="Search port or country..."
                      data-testid="origin-search"
                    />
                    {showOriginDropdown && filteredOriginPorts.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredOriginPorts.map((port, idx) => (
                          <div
                            key={`${port.code}-${idx}`}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectOriginPort(port)}
                          >
                            <div className="font-medium">{port.name} ({port.code})</div>
                            <div className="text-slate-500 text-xs">{port.country} • {port.type}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 relative">
                    <Label className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-red-500" />
                      Destination Port *
                    </Label>
                    <Input
                      value={destSearch}
                      onChange={(e) => {
                        setDestSearch(e.target.value);
                        setShowDestDropdown(true);
                      }}
                      onFocus={() => setShowDestDropdown(true)}
                      placeholder="Search port or country..."
                      data-testid="dest-search"
                    />
                    {showDestDropdown && filteredDestPorts.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredDestPorts.map((port, idx) => (
                          <div
                            key={`${port.code}-${idx}`}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectDestPort(port)}
                          >
                            <div className="font-medium">{port.name} ({port.code})</div>
                            <div className="text-slate-500 text-xs">{port.country} • {port.type}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Mode & Equipment */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Transport Mode *</Label>
                    <Select value={rateForm.mode} onValueChange={(v) => setRateForm(prev => ({ ...prev, mode: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sea">Sea Freight</SelectItem>
                        <SelectItem value="air">Air Freight</SelectItem>
                        <SelectItem value="land">Land/Trucking</SelectItem>
                        <SelectItem value="multimodal">Multimodal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Equipment/Cargo Type *</Label>
                    <Select value={rateForm.equipment_type} onValueChange={(v) => setRateForm(prev => ({ ...prev, equipment_type: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <SelectItem value="header_containers" disabled className="font-bold text-slate-500">── Containers ──</SelectItem>
                        {equipmentTypes.filter(e => e.category === 'container').map(eq => (
                          <SelectItem key={eq.value} value={eq.value}>{eq.label}</SelectItem>
                        ))}
                        <SelectItem value="header_tanks" disabled className="font-bold text-slate-500">── Tank Containers ──</SelectItem>
                        {equipmentTypes.filter(e => e.category === 'tank').map(eq => (
                          <SelectItem key={eq.value} value={eq.value}>{eq.label}</SelectItem>
                        ))}
                        <SelectItem value="header_vehicles" disabled className="font-bold text-slate-500">── Vehicles ──</SelectItem>
                        {equipmentTypes.filter(e => e.category === 'vehicle').map(eq => (
                          <SelectItem key={eq.value} value={eq.value}>{eq.label}</SelectItem>
                        ))}
                        <SelectItem value="header_other" disabled className="font-bold text-slate-500">── Other ──</SelectItem>
                        {equipmentTypes.filter(e => !['container', 'tank', 'vehicle'].includes(e.category)).map(eq => (
                          <SelectItem key={eq.value} value={eq.value}>{eq.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Rate & Currency */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Rate *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={rateForm.rate}
                      onChange={(e) => setRateForm(prev => ({ ...prev, rate: e.target.value }))}
                      placeholder="0.00"
                      required
                      data-testid="rate-amount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={rateForm.currency} onValueChange={(v) => setRateForm(prev => ({ ...prev, currency: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="CAD">CAD</SelectItem>
                        <SelectItem value="AUD">AUD</SelectItem>
                        <SelectItem value="JPY">JPY</SelectItem>
                        <SelectItem value="CNY">CNY</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Transit Time (days)</Label>
                    <Input
                      type="number"
                      value={rateForm.transit_time_days}
                      onChange={(e) => setRateForm(prev => ({ ...prev, transit_time_days: e.target.value }))}
                      placeholder="e.g., 14"
                    />
                  </div>
                </div>

                {/* Validity Period */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <Label className="flex items-center gap-2 mb-3 text-amber-800">
                    <Calendar className="h-4 w-4" />
                    Rate Validity Period *
                  </Label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-amber-700">Valid From</Label>
                      <Input
                        type="date"
                        value={rateForm.valid_from}
                        onChange={(e) => setRateForm(prev => ({ ...prev, valid_from: e.target.value }))}
                        required
                        data-testid="valid-from"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-amber-700">Valid Until (Expiration)</Label>
                      <Input
                        type="date"
                        value={rateForm.valid_until}
                        onChange={(e) => setRateForm(prev => ({ ...prev, valid_until: e.target.value }))}
                        required
                        data-testid="valid-until"
                      />
                    </div>
                  </div>
                </div>

                {/* Optional Fields */}
                <div className="space-y-2">
                  <Label>Carrier/Shipping Line (Optional)</Label>
                  <Input
                    value={rateForm.carrier}
                    onChange={(e) => setRateForm(prev => ({ ...prev, carrier: e.target.value }))}
                    placeholder="e.g., Maersk, MSC, CMA CGM"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={rateForm.notes}
                    onChange={(e) => setRateForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes, terms, or conditions..."
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowRateForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600" data-testid="save-rate-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {editingRate ? 'Update Rate' : 'Add Rate'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Expiring Rates Modal */}
          <Dialog open={showExpiringModal} onOpenChange={setShowExpiringModal}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                  Rates Expiring Soon ({expiringRates.length})
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-4 max-h-[60vh] overflow-y-auto">
                {expiringRates.map((rate, idx) => (
                  <div key={rate.id || idx} className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-bold">{rate.origin_port || rate.lane?.split('-')[0]} → {rate.destination_port || rate.lane?.split('-')[1]}</div>
                        <div className="text-sm text-slate-600">{getEquipmentLabel(rate.equipment_type || rate.cargo_type)}</div>
                        <div className="text-sm text-amber-700 mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Expires: {new Date(rate.valid_until).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">{rate.currency} {rate.rate || rate.avg_rate}</div>
                        <Button size="sm" variant="outline" onClick={() => { setShowExpiringModal(false); editRate(rate); }}>
                          <Edit className="h-3 w-3 mr-1" />
                          Update
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => openRoutesModal('all', 'All Active Rates')}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-ocean-900">{displayInsights.length}</p>
                <p className="text-sm text-slate-600">Active Rates</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => openRoutesModal('sea', 'Sea Freight Rates')}>
              <CardContent className="p-4 text-center">
                <Ship className="h-5 w-5 mx-auto mb-1 text-cyan-500" />
                <p className="text-2xl font-bold text-cyan-600">{displayInsights.filter(i => i.mode === 'sea').length}</p>
                <p className="text-sm text-slate-600">Sea Routes</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => openRoutesModal('air', 'Air Freight Rates')}>
              <CardContent className="p-4 text-center">
                <Plane className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                <p className="text-2xl font-bold text-blue-600">{displayInsights.filter(i => i.mode === 'air').length}</p>
                <p className="text-sm text-slate-600">Air Routes</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Container className="h-5 w-5 mx-auto mb-1 text-purple-500" />
                <p className="text-2xl font-bold text-purple-600">
                  {displayInsights.filter(i => (i.equipment_type || '').includes('ft')).length}
                </p>
                <p className="text-sm text-slate-600">Container Rates</p>
              </CardContent>
            </Card>
          </div>

          {/* Routes Modal */}
          <Dialog open={showRoutesModal} onOpenChange={setShowRoutesModal}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{routesModalTitle}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                {getFilteredRoutesForModal().length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No rates found</p>
                ) : (
                  getFilteredRoutesForModal().map((rate, idx) => (
                    <div key={rate.id || idx} className="border rounded-lg p-4 hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-lg">
                              {rate.origin_port || rate.lane?.split('-')[0]} → {rate.destination_port || rate.lane?.split('-')[1]}
                            </span>
                            <Badge className={getModeColor(rate.mode)}>
                              {getModeIcon(rate.mode)}
                              <span className="ml-1 capitalize">{rate.mode}</span>
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {getEquipmentIcon(rate.equipment_type || rate.cargo_type)}
                            <span className="text-sm text-slate-600">{getEquipmentLabel(rate.equipment_type || rate.cargo_type)}</span>
                          </div>
                          {rate.valid_until && (
                            <div className={`text-xs mt-1 ${isRateExpiringSoon(rate) ? 'text-amber-600' : 'text-slate-500'}`}>
                              Valid until: {new Date(rate.valid_until).toLocaleDateString()}
                              {isRateExpiringSoon(rate) && ' (Expiring soon!)'}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-cyan-600">
                            {rate.currency} {(rate.rate || rate.avg_rate)?.toLocaleString()}
                          </p>
                          {rate.transit_time_days && (
                            <p className="text-xs text-slate-500">{rate.transit_time_days} days transit</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Rate Cards Grid */}
          {displayInsights.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-slate-600">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p className="mb-2">No rate insights available</p>
                <p className="text-sm">Add freight rates to start tracking market prices.</p>
                {isAdmin && (
                  <Button className="mt-4 bg-cyan-500 hover:bg-cyan-600" onClick={() => setShowRateForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Rate
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayInsights.map((rate, index) => (
                <Card 
                  key={rate.id || index} 
                  className={`hover:shadow-lg transition-shadow ${isRateExpiringSoon(rate) ? 'border-amber-300' : ''}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-mono">
                        {rate.origin_port || rate.lane?.split('-')[0]} → {rate.destination_port || rate.lane?.split('-')[1]}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge className={getModeColor(rate.mode)}>
                          {getModeIcon(rate.mode)}
                        </Badge>
                        {isAdmin && rate.source !== 'aggregate' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => editRate(rate)} className="h-6 w-6 p-0">
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteRate(rate.id)} className="h-6 w-6 p-0 text-red-500">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      {getEquipmentIcon(rate.equipment_type || rate.cargo_type)}
                      <span>{getEquipmentLabel(rate.equipment_type || rate.cargo_type)}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center p-4 bg-cyan-50 rounded-lg mb-4">
                      <p className="text-sm text-cyan-700 mb-1">Rate</p>
                      <p className="text-3xl font-bold text-cyan-600">
                        {rate.currency} {(rate.rate || rate.avg_rate)?.toLocaleString()}
                      </p>
                    </div>

                    {rate.transit_time_days && (
                      <div className="text-sm text-slate-600 mb-2">
                        Transit: {rate.transit_time_days} days
                      </div>
                    )}

                    {rate.carrier && (
                      <div className="text-sm text-slate-600 mb-2">
                        Carrier: {rate.carrier}
                      </div>
                    )}

                    {rate.notes && (
                      <p className="text-xs text-slate-500 italic mb-3">{rate.notes}</p>
                    )}

                    {/* Validity */}
                    <div className={`text-xs p-2 rounded ${isRateExpiringSoon(rate) ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                      {isRateExpiringSoon(rate) && <AlertTriangle className="h-3 w-3 inline mr-1" />}
                      {rate.valid_from && rate.valid_until ? (
                        <>Valid: {new Date(rate.valid_from).toLocaleDateString()} - {new Date(rate.valid_until).toLocaleDateString()}</>
                      ) : (
                        rate.last_updated && <>Updated: {new Date(rate.last_updated).toLocaleDateString()}</>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FreightInsights;
